import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/common/Panel";
import { StatusBadge } from "@/components/common/StatusBadge";
import { TextField, SelectField } from "@/components/common/Field";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { formatDate } from "@/lib/format";
import { Plus, Star, GitFork } from "lucide-react";
import { GovernancePanel } from "./GovernancePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KINDS = ["Suit", "Writ", "Appeal", "Revision", "Execution", "Arbitration", "Complaint", "Application", "Review", "SLP", "Other"];
const STATUSES = ["Pending", "Disposed", "Stayed", "Withdrawn", "Transferred"];
const err = (e, f) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || f; };

export function ProceedingsTab({ matter }) {
  const { can } = useApp();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => axios.get(`${API}/matters/${matter.id}/proceedings`).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matter.id]);

  const setPrimary = async (p) => {
    try { await axios.post(`${API}/matters/${matter.id}/proceedings/${p.id}/primary`); toast.success(`${p.caseNumber} is now the live proceeding.`); load(); }
    catch (e) { toast.error(err(e, "Could not update.")); }
  };
  const setStatus = async (p, status) => {
    try { await axios.patch(`${API}/matters/${matter.id}/proceedings/${p.id}`, { status, expectedVersion: p.version }); load(); }
    catch (e) { toast.error(err(e, "Could not update.")); }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="space-y-3 lg:col-span-2" data-testid="proceedings-tab">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold text-ink">Proceedings</h3>
            <p className="text-xs text-ink-2">Original record <b>{matter.caseNumber}</b> stays fixed. Appeals, executions and arbitrations get their own case numbers here.</p>
          </div>
          {can("matter.edit") && <Button size="sm" onClick={() => setOpen(true)} data-testid="add-proceeding-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Add Proceeding</Button>}
        </div>
        <Panel>
          {rows === null ? <div className="p-6 text-sm text-ink-muted">Loading…</div> : (
            <div className="divide-y divide-line">
              {rows.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`proceeding-row-${p.id}`}>
                  <GitFork className="h-4 w-4 shrink-0 text-copper" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-ink">
                      <span data-testid={`proceeding-case-${p.id}`}>{p.caseNumber}</span>
                      <span className="rounded-full bg-copper-light px-2 py-0.5 text-[10px] font-medium text-copper">{p.kind}</span>
                      {p.isPrimary && <span className="flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium text-success" data-testid={`proceeding-primary-${p.id}`}><Star className="h-3 w-3" /> Live</span>}
                    </div>
                    <div className="text-xs text-ink-2">{p.court} · {p.stage} · filed {formatDate(p.filedOn)}{p.parentProceedingId && " · arises from " + (rows.find((x) => x.id === p.parentProceedingId)?.caseNumber || "earlier proceeding")}</div>
                  </div>
                  <StatusBadge status={p.status} />
                  {can("matter.edit") && (
                    <div className="flex items-center gap-1">
                      <select className="h-7 rounded border border-line bg-card px-1 text-xs" value={p.status} onChange={(e) => setStatus(p, e.target.value)} data-testid={`proceeding-status-${p.id}`}>
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                      {!p.isPrimary && <Button variant="ghost" size="sm" onClick={() => setPrimary(p)} data-testid={`proceeding-set-primary-${p.id}`}>Make live</Button>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
      <GovernancePanel matter={matter} />
      <AddProceedingDialog open={open} onOpenChange={setOpen} matter={matter} parents={rows || []} onDone={load} />
    </div>
  );
}

function AddProceedingDialog({ open, onOpenChange, matter, parents, onDone }) {
  const [f, setF] = useState({ kind: "Appeal", caseNumber: "", court: matter.court, stage: "Filing", filedOn: "", parentProceedingId: "", isPrimary: false });
  const [error, setError] = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async () => {
    if (!f.caseNumber.trim()) { setError("Case number is required"); return; }
    try {
      await axios.post(`${API}/matters/${matter.id}/proceedings`, { ...f, parentProceedingId: f.parentProceedingId || null, filedOn: f.filedOn || undefined });
      toast.success("Proceeding added."); onOpenChange(false); setF((x) => ({ ...x, caseNumber: "" })); onDone();
    } catch (e) { setError(err(e, "Could not add proceeding.")); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-proceeding-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">New Proceeding</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <SelectField label="Kind" value={f.kind} onChange={(v) => set("kind", v)} options={KINDS} testid="proceeding-kind" />
          <TextField label="Case Number" required value={f.caseNumber} onChange={(v) => set("caseNumber", v)} placeholder="e.g. WA 112/2026" testid="proceeding-case-number" error={error} />
          <TextField label="Court / Forum" value={f.court} onChange={(v) => set("court", v)} />
          <TextField label="Stage" value={f.stage} onChange={(v) => set("stage", v)} />
          <TextField label="Filed On" type="date" value={f.filedOn} onChange={(v) => set("filedOn", v)} />
          <SelectField label="Arises From" value={f.parentProceedingId || "none"} onChange={(v) => set("parentProceedingId", v === "none" ? "" : v)} options={[{ value: "none", label: "— none —" }, ...parents.map((p) => ({ value: p.id, label: `${p.caseNumber} (${p.kind})` }))]} />
          <label className="col-span-2 flex items-center gap-2 text-sm text-ink-2"><input type="checkbox" checked={f.isPrimary} onChange={(e) => set("isPrimary", e.target.checked)} data-testid="proceeding-make-live" /> Make this the live proceeding for dashboards</label>
        </div>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} data-testid="proceeding-submit">Add Proceeding</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
